/**
 * The delete-account dialog of the settings screen (port addition: app stores require that a player can delete the
 * account from inside the game; the backend's `account.delete`, docs/backend.md §9.1).
 *
 * A modal popup over the settings screen: a dimmed stage that swallows the mouse, the frame of the game's popups
 * (menu_tired, as the sign-in screen's) and in it
 *  - the title, the account it deletes (the nickname is data: never translated) and what goes, for good;
 *  - for an account with a password: its password field, which confirms the deletion (account.delete checks it like a
 *    sign-in: a wrong one gets UNAUTHORIZED 'invalid_credentials' and the session stays); a guest of an earlier
 *    version of the game has no password and deletes with its session alone, so its dialog says that instead;
 *  - "Delete for good" (the beige confirmation button of the game's questions) and "cancel".
 *
 * Behaviour:
 *  - Enter confirms, Escape and the Android back button cancel (not while the request is in flight);
 *  - an empty password is refused without a request; while the request is in flight the button says "Please wait…"
 *    and every control is disabled, so a second request is impossible; a refusal re-enables the dialog with its
 *    message (wrong password, too many attempts, no connection, ...) for another try;
 *  - the password lives only in its field and in the one request: the field is emptied when the request is sent and
 *    when the dialog closes, and it is never copied into any state of the dialog or of the game;
 *  - the field is the game's own input field, so a phone opens its keyboard for it (a password <input> of the page:
 *    src/flash/platform/browser-input.ts); a wide screen (a phone, a tablet) shows the whole frame larger, as the
 *    game's popups (ui/port-dialog.ts), a small screen without the wide view zooms into the dialog (focusRect, as the
 *    sign-in form); the settings screen under it is hidden while it is open (no pieces of it round the dialog).
 * The game (FWP.net_delete_account_prompt) signs out locally and restarts into the sign-in screen once it succeeded.
 */
import { Event, KeyboardEvent, Point, Sprite, TextField } from '@flash';
import { PASSWORD_MAX } from '../../shared/auth-rules';
import * as T from '../runtime/auth-texts';
import { playerName, translate } from '../runtime/i18n';
import { netErrorCode } from '../runtime/net-policy';
import { failureText } from './auth-screen';
import { BigButton, ERROR_COLOR, LINK_COLOR, SmallButton, TEXT_COLOR, inputField, label, popupFrame, setRawText, shrinkToFit } from './controls';
import { fitDialogBody, hideScreenUnder } from './port-dialog';

export interface DeleteAccountOptions {
  /** The account is gone: the backend services dropped the session and wiped the stored credentials. */
  onDeleted(): void;
  /** The session ended meanwhile (UNAUTHORIZED other than a wrong password): the game handles it like any ended session. */
  onSessionEnded(): void;
  /** The dialog was closed without deleting anything. */
  onClosed(): void;
}

/** The frame (menu_tired's at 0.8, 344 x 389) in the middle of the 807 x 600 stage. */
const PANEL_SCALE = 0.8;
const FRAME_W = 344;
const FRAME_H = 389;
const FRAME_X = Math.round((807 - FRAME_W) / 2);
const FRAME_Y = Math.round((600 - FRAME_H) / 2);
/** The content column inside the frame (frame coordinates; the beanstalk decoration covers the frame's right edge). */
const COL_X = 26;
const COL_W = 270;
const FOCUS_MARGIN = 14;
const FOCUS_BOTTOM = 345;
/** The title: the dark red of a destructive action (the game's error red is for messages). */
const TITLE_COLOR = 0xb00000;
/** A dimmed stage behind the popup, big enough for the widest stage (src/shared/wide-view.ts). */
const DIM = { x: -2000, y: -1000, w: 4807, h: 2600, alpha: 0.5 };

export class DeleteAccountDialog {
  /** Everything of the dialog (added to the game's system layer by FWP.net_delete_account_prompt). */
  readonly root = new Sprite();
  /** The request is in flight. */
  busy = false;
  /** account.delete requests sent (tests: no double submit). */
  requests = 0;
  readonly guest: boolean;
  readonly password: TextField | null = null;
  readonly status: TextField;
  readonly confirmButton: BigButton;
  readonly cancelButton: SmallButton;

  private disposed = false;
  private stageRef: any = null;
  /** The frame and the content (not the dim): scaled on a wide screen. */
  private readonly body = new Sprite();
  /** Shows the settings screen under the dialog again. */
  private readonly restoreUnder: () => void;

  constructor(
    private readonly fwp: any,
    private readonly opts: DeleteAccountOptions,
  ) {
    const user = fwp.user || {};
    this.guest = !!user.guest;
    this.root.name = '$delete_account';
    const dim = new Sprite();
    dim.name = '$delete_account_dim';
    dim.graphics.beginFill(0x000000, DIM.alpha);
    dim.graphics.drawRect(DIM.x, DIM.y, DIM.w, DIM.h);
    dim.graphics.endFill();
    this.root.addChild(dim);
    // The frame and the content: one body, shown larger on a wide screen (fitDialogBody).
    this.body.name = '$delete_account_body';
    this.root.addChild(this.body);
    const panel = popupFrame(fwp, FRAME_X, FRAME_Y, PANEL_SCALE);
    panel.name = '$delete_account_panel';
    this.body.addChild(panel);
    const page = new Sprite();
    page.name = '$delete_account_content';
    page.x = FRAME_X;
    page.y = FRAME_Y;
    this.body.addChild(page);
    this.restoreUnder = hideScreenUnder(fwp);

    place(page, named(label(T.MSG_DELETE_TITLE, { size: 14, bold: true, color: TITLE_COLOR, align: 'center', width: COL_W }), 'delete_title'), COL_X, 20);
    const account = named(label('', { size: 12, align: 'center', width: COL_W }), 'delete_nickname');
    // The nickname is the player's own and stays as typed; a guest's default name is game text.
    const name = String(user.name ?? '');
    setRawText(account, `${translate(T.MSG_DELETE_ACCOUNT_OF)} ${this.guest ? playerName(name) : name}`);
    place(page, account, COL_X, 44);
    const warning = named(label(T.MSG_DELETE_WARNING, { size: 12, color: TEXT_COLOR, width: COL_W, lines: 6 }), 'delete_warning');
    shrinkToFit(warning, 10);
    place(page, warning, COL_X, 66);
    if (this.guest) {
      const guest = named(label(T.MSG_DELETE_GUEST, { size: 12, bold: true, color: TEXT_COLOR, width: COL_W, lines: 3 }), 'delete_guest');
      guest.height = 52;
      shrinkToFit(guest, 10);
      place(page, guest, COL_X, 164);
    } else {
      const prompt = named(label(T.MSG_DELETE_PASSWORD, { size: 12, bold: true, width: COL_W, lines: 2 }), 'delete_password_label');
      shrinkToFit(prompt, 10);
      place(page, prompt, COL_X, 162);
      this.password = inputField('delete_password', { width: COL_W, maxChars: PASSWORD_MAX, password: true });
      place(page, this.password, COL_X, 196);
      this.password.addEventListener(Event.CHANGE, () => {
        if (!this.busy) this.status.text = '';
      });
    }
    this.status = named(label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 2 }), 'delete_status');
    // Two whole lines of bold text with the field's gutters (the default height cuts the second line's descenders).
    this.status.height = 36;
    place(page, this.status, COL_X, 223);
    this.confirmButton = new BigButton(fwp, 'delete_confirm', T.MSG_DELETE_CONFIRM, 'beige');
    this.confirmButton.addEventListener('click', () => this.submit());
    place(page, this.confirmButton, COL_X + (COL_W - this.confirmButton.width) / 2, 262);
    this.cancelButton = new SmallButton(fwp, 'delete_cancel', T.MSG_DELETE_CANCEL);
    this.cancelButton.addEventListener('click', () => this.cancel());
    place(page, this.cancelButton, COL_X + (COL_W - this.cancelButton.width) / 2, 312);
    // A finger's target from under the confirmation button to the frame's content bottom, across the column.
    this.cancelButton.setHitRect({ x: COL_X - this.cancelButton.x, y: 305 - 312, width: COL_W, height: FOCUS_BOTTOM - 305 });
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
  }

  /** Starts listening to the keyboard and focuses the password field (once the dialog is on the stage). */
  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    if (this.password) st.focus = this.password;
  }

  /**
   * The dialog's column in stage coordinates (with a small margin), or null when it is not shown: a phone zooms into
   * it (the browser host's focusRect, src/main.ts), as into the sign-in form.
   */
  focusRect(): { x: number; y: number; width: number; height: number } | null {
    if (this.disposed || !this.root.stage) return null;
    // (a wide host shows the whole frame larger itself: ui/port-dialog.ts)
    if ((this.root.stage as any).$visibleArea) return null;
    const a = this.root.localToGlobal(new Point(FRAME_X + COL_X - FOCUS_MARGIN, FRAME_Y + 4));
    const b = this.root.localToGlobal(new Point(FRAME_X + COL_X + COL_W + FOCUS_MARGIN, FRAME_Y + FOCUS_BOTTOM));
    return { x: Math.round(a.x), y: Math.round(a.y), width: Math.round(b.x - a.x), height: Math.round(b.y - a.y) };
  }

  /** Back (the Android back button): cancels unless the request is in flight. Always handled while open. */
  back(): boolean {
    if (!this.disposed && !this.busy) this.cancel();
    return true;
  }

  /** Closes the dialog without deleting anything. */
  cancel(): void {
    if (this.busy || this.disposed) return;
    this.dispose();
    this.opts.onClosed();
  }

  /** Removes the dialog and its listeners and empties the password field (the dialog is gone for good). */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.stageRef) {
      this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
      if (this.stageRef.focus === this.password) this.stageRef.focus = null;
    }
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    if (this.password) this.password.text = '';
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.restoreUnder();
  }

  private readonly onKey = (e: any): void => {
    if (this.disposed) return;
    if (e.keyCode === 13) this.submit();
    else if (e.keyCode === 27) this.cancel();
  };

  /** Confirms (Enter or the button): one request at a time; the password leaves its field for that request only. */
  submit(): void {
    if (this.busy || this.disposed) return;
    let password: string | undefined = undefined;
    if (this.password) {
      if (this.password.text === '') {
        this.showError(T.CREDENTIAL_PROBLEM_TEXT.password_required);
        return;
      }
      password = this.password.text;
      this.password.text = '';
    }
    this.status.text = '';
    this.setBusy(true);
    this.requests++;
    Promise.resolve(this.fwp.services.deleteAccount(password)).then(
      () => this.succeeded(),
      (error: any) => this.failed(error),
    );
  }

  /** A message in the status line (the error colour) and the password field focused again. */
  showError(text: string): void {
    this.status.textColor = ERROR_COLOR;
    this.status.text = text;
    const st = this.root.stage;
    if (st && this.password) st.focus = this.password;
  }

  private succeeded(): void {
    if (this.disposed) return;
    this.busy = false;
    this.dispose();
    this.opts.onDeleted();
  }

  private failed(error: any): void {
    if (this.disposed) return;
    this.setBusy(false);
    const code = netErrorCode(error);
    const details = error && error.details ? error.details : {};
    const reason = typeof details.reason === 'string' ? details.reason : '';
    if (code === 'UNAUTHORIZED' && reason === 'invalid_credentials') return this.showError(T.MSG_DELETE_WRONG_PASSWORD);
    if (code === 'VALIDATION_FAILED' && reason === 'password_required') return this.showError(T.CREDENTIAL_PROBLEM_TEXT.password_required);
    if (code === 'RATE_LIMITED' && reason === 'too_many_attempts') return this.showError(T.MSG_DELETE_TOO_MANY);
    // No session any more (it ended elsewhere, or the reply of a deletion that went through was lost) or no account:
    // this run of the game cannot go on.
    if (code === 'UNAUTHORIZED' || (code === 'NOT_FOUND' && reason === 'account_not_found')) {
      this.dispose();
      this.opts.onSessionEnded();
      return;
    }
    const text = failureText(code, reason);
    this.showError(text === T.MSG_AUTH_FAILED ? T.MSG_DELETE_FAILED : text);
  }

  private setBusy(on: boolean): void {
    this.busy = on;
    this.confirmButton.enabled = !on;
    this.confirmButton.setText(on ? T.MSG_AUTH_WAIT : T.MSG_DELETE_CONFIRM);
    this.cancelButton.enabled = !on;
    if (this.password) {
      this.password.type = on ? 'dynamic' : 'input';
      this.password.selectable = !on;
      const st = this.root.stage;
      if (on && st && st.focus === this.password) st.focus = null;
    }
  }

  private readonly onFrame = (): void => {
    const st = this.root.stage;
    fitDialogBody(this.body, { x: FRAME_X, y: FRAME_Y, width: FRAME_W, height: FRAME_H }, st);
    // The focused field gets the green frame of the sign-in form.
    const f = this.password;
    if (!f) return;
    const color = st && st.focus === f ? LINK_COLOR : 0x000000;
    if (f.borderColor !== color) f.borderColor = color;
  };
}

function place(parent: Sprite, child: any, x: number, y: number): void {
  child.x = Math.round(x);
  child.y = Math.round(y);
  parent.addChild(child);
}

function named<O extends { name: string }>(o: O, name: string): O {
  o.name = name;
  return o;
}
